import React from "react";
import { Drawer, Select, Input, Switch, Button, Tag, Space, Tooltip } from "antd";
import {
  DeleteOutlined,
  EyeOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import SelectWithAdd from "../generic/SelectWithAdd";
import { useMediaQuery } from "../../helper/useMediaQuery";
import { labelForCategory } from "../../constants/reconciliationLabels";
import "../../styles/custom-dropdown.css";
import "../../styles/custom-switch.css";

const { Option } = Select;

function Field({ label, children, error }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <p className="spec-modal-label" style={{ color: "#888", marginBottom: 6 }}>
        {label}
      </p>
      {children}
      {/* Inline, next to the field it concerns — the old global toast fired after the
          graph had already changed, which told the user nothing actionable. */}
      {error && (
        <p style={{ color: "#a32d2d", fontSize: 12, margin: "6px 0 0" }}>{error}</p>
      )}
    </div>
  );
}

// Edit a single workflow step. Drives the shared useMasterSuiteStepEditor handlers
// so it stays in lock-step with the table; the Parameters / Task modals are hosted
// by the workspace and triggered through the same hook.
function StepEditDrawer({ editor, open, onClose }) {
  const {
    draft,
    draftErrors,
    draftValid,
    commitDraft,
    dataSource,
    allCategories,
    handleTypeChange,
    handleCategoryChange,
    handleSubcategoryChange,
    handleStepReferenceChange,
    handleParentChange,
    parentOptionsFor,
    siblingsOf,
    handleTaskNameChange,
    handleSwitchChange,
    handleVariablesClick,
    handleAddTaskItem,
    onClickViewTask,
    handleDelete,
    moveStep,
  } = editor;

  // Fixed width on desktop; full-width below 1024px so the drawer is usable on
  // small screens (the canvas defaults to the Table view there anyway).
  const isNarrow = useMediaQuery("(max-width: 1024px)");

  // The drawer edits the DRAFT, never a committed row. That is the whole boundary: the
  // canvas renders dataSource and cannot see anything typed here until Save.
  const record = draft?.row || null;
  const isNew = draft?.mode === "new";
  const index = record ? dataSource.findIndex((r) => r.key === record.key) : -1;
  const err = draftErrors || {};

  const onDelete = () => {
    if (record) handleDelete(record.key);
    onClose();
  };

  const onSave = () => {
    if (commitDraft()) onClose();
  };

  // Steps sharing this one's parent start at the same moment. Worth stating outright:
  // "runs after X" on its own doesn't tell you that something else also runs after X.
  const parallelWith = record ? siblingsOf(record) : [];

  return (
    <Drawer
      title={
        !record
          ? "Step"
          : isNew
            ? "New step"
            : `Step ${record.sequence}${
                record.stepReference ? ` · ${record.stepReference}` : ""
              }`
      }
      // Close, Esc and the mask all land here, and all of them DISCARD. Closing is always
      // safe — there is nothing half-written in the graph to leave behind.
      maskClosable
      footer={
        record && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Tooltip
              title={
                draftValid ? "" : "Complete the required fields to save this step"
              }
            >
              {/* span so the tooltip still fires while the button is disabled */}
              <span>
                <Button type="primary" onClick={onSave} disabled={!draftValid}>
                  {isNew ? "Add step" : "Save"}
                </Button>
              </span>
            </Tooltip>
          </div>
        )
      }
      placement="right"
      width={isNarrow ? "100%" : 460}
      open={open}
      onClose={onClose}
      extra={
        // Reorder acts on committed rows, so it is meaningless for a step that is not in
        // the suite yet. Hidden rather than disabled — a new step has no position to move.
        record && !isNew && (
          <Space>
            <Tooltip title="Move up">
              <Button
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={index <= 0}
                onClick={() => moveStep(index, index - 1)}
              />
            </Tooltip>
            <Tooltip title="Move down">
              <Button
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={index < 0 || index >= dataSource.length - 1}
                onClick={() => moveStep(index, index + 1)}
              />
            </Tooltip>
          </Space>
        )
      }
    >
      {!record ? null : (
        <>
          <Field label="Step Name *" error={err.stepReference}>
            <Input
              value={record.stepReference || ""}
              status={err.stepReference ? "error" : undefined}
              onChange={(e) =>
                handleStepReferenceChange(e.target.value, record)
              }
              placeholder="Enter step name"
              maxLength={50}
            />
          </Field>

          <Field label="Type *" error={err.type}>
            <Select
              style={{ width: "100%" }}
              className="custom-select"
              status={err.type ? "error" : undefined}
              value={record.type || undefined}
              onChange={(value) => handleTypeChange(value, record)}
              placeholder="Type"
            >
              <Option value="Master Suite">Master Suite</Option>
              <Option value="Task">Task</Option>
            </Select>
          </Field>

          <Field label="Category *" error={err.category}>
            <Select
              style={{ width: "100%" }}
              className="custom-select"
              status={err.category ? "error" : undefined}
              value={record.type === "Master Suite" ? "N/A" : record.category}
              onChange={(value) => handleCategoryChange(value, record)}
              placeholder="Category"
              // Display-only rename ("compare" → "Reconciliation"); onChange stores
              // the option value, so the persisted category is unchanged.
              options={allCategories.map((c) => ({
                ...c,
                label: labelForCategory(c.label),
              }))}
              disabled={!record.type || record.type === "Master Suite"}
            />
          </Field>

          <Field label="Sub Category *" error={err.subcategory}>
            {record.category === "ui" ? (
              <Tag color="purple" style={{ margin: 0 }}>
                UI Test
              </Tag>
            ) : (
              <Select
                style={{ width: "100%" }}
                className="custom-select"
                value={
                  record.type === "Master Suite" ? "N/A" : record.subcategory
                }
                onChange={(value) => handleSubcategoryChange(value, record)}
                status={err.subcategory ? "error" : undefined}
                placeholder="Sub Category"
                options={record.subCategoryOptions || []}
                disabled={!record.category || record.category === "N/A"}
              />
            )}
          </Field>

          <Field
            label={record.category === "ui" ? "UI Suite *" : "Task *"}
            error={err.taskName}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <SelectWithAdd
                placeholder={
                  record.category === "ui" ? "select UI suite" : "task name"
                }
                options={record.filteredTaskArray || []}
                value={record.taskName || undefined}
                onSelect={(value) => handleTaskNameChange(value, record)}
                labelKey="taskName"
                valueKey="taskName"
                addItem={() => handleAddTaskItem(record)}
                buttonDisabled={
                  !record.type ||
                  record.type === "Master Suite" ||
                  record.category === "ui"
                }
                disabled={!record.subcategory}
              />
              {record.category !== "ui" && (
                <Tooltip title="View task">
                  <Button
                    type="text"
                    icon={<EyeOutlined style={{ color: "gray" }} />}
                    onClick={() => onClickViewTask(record)}
                  />
                </Tooltip>
              )}
            </div>
          </Field>

          <Field label="Parameters">
            {record.category === "ui" ? (
              <span style={{ color: "#bbb", fontSize: 12 }}>
                UI Automation steps don't use parameters.
              </span>
            ) : (
              <Button
                icon={<SettingOutlined />}
                onClick={() => handleVariablesClick(record)}
              >
                {record.variables && Object.keys(record.variables).length > 0
                  ? "Edit parameters"
                  : "Add parameters"}
              </Button>
            )}
          </Field>

          <Field label="Runs after">
            <Select
              style={{ width: "100%" }}
              className="custom-select"
              allowClear
              // Only steps that can legally be a parent: not this one, and nothing
              // already beneath it. Excluding descendants here is what makes a cycle
              // unreachable rather than something to detect afterwards.
              value={record.parentId || undefined}
              onChange={(value) => handleParentChange(value ?? null, record)}
              placeholder="Start of workflow"
              options={parentOptionsFor(record).map((r) => ({
                value: r.stepReference,
                label: `${r.sequence}. ${r.stepReference}`,
              }))}
              notFoundContent="No step is available to run before this one"
            />
            <p style={{ color: "#888", fontSize: 12, margin: "6px 0 0" }}>
              {!record.parentId
                ? "Starts as soon as the run begins."
                : `Waits for "${record.parentId}" to finish.`}
              {parallelWith.length > 0 && (
                <>
                  {" "}
                  Runs at the same time as{" "}
                  <strong>
                    {parallelWith.map((r) => r.stepReference).join(", ")}
                  </strong>
                  .
                </>
              )}
            </p>
          </Field>

          <Field label="Stop if Failed">
            <Switch
              className="custom-switch"
              checked={record.stopIfFailed}
              onChange={(checked) => handleSwitchChange(checked, record)}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
            <p style={{ color: "#888", fontSize: 12, margin: "6px 0 0" }}>
              {record.stopIfFailed
                ? "If this step fails, the steps that follow it are skipped. Other branches keep running."
                : "The run carries on even if this step fails."}
            </p>
          </Field>

          {/* Only for a committed step. Cancel is how you abandon a new one — there is
              nothing in the graph to delete. */}
          {!isNew && (
            <Button danger icon={<DeleteOutlined />} onClick={onDelete}>
              Delete step
            </Button>
          )}
        </>
      )}
    </Drawer>
  );
}

export default StepEditDrawer;
